import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Minus, Plus, X, Loader2 } from "lucide-react";
import { formatPrice } from "@/lib/format";

const EMPTY_CUST = { name: "", email: "", phone: "", address: "" };

export const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [cust, setCust] = useState(EMPTY_CUST);

  const setField = (k, v) => setCust((c) => ({ ...c, [k]: v }));

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const checkout = async () => {
    if (items.length === 0) return;
    if (!cust.name.trim() || !cust.email.includes("@")) {
      toast.error("Please enter your name and a valid email");
      return;
    }
    if (!cust.address.trim()) {
      toast.error("Please enter your shipping address");
      return;
    }
    setLoading(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Razorpay SDK failed to load");
      const { data } = await api.post("/payments/razorpay/order", {
        items: items.map((i) => ({ id: i.id, quantity: i.quantity, size: i.size })),
        customer: cust,
      });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "House of AAK",
        description: "Ethnic Couture",
        prefill: { name: data.name, email: data.email, contact: data.contact },
        theme: { color: "#8C5E45" },
        handler: async (res) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            window.location.href = `/payment/success?session_id=${res.razorpay_order_id}`;
          } catch {
            window.location.href = "/payment/cancel";
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-aak-bg border-l border-aak-border rounded-none p-0 flex flex-col [&>button]:hidden"
        data-testid="cart-drawer"
      >
        <SheetHeader className="p-6 border-b border-aak-border flex-row items-center justify-between space-y-0">
          <SheetTitle className="font-serif text-2xl text-aak-fg font-normal">
            Your Bag <span className="text-aak-mutedfg text-base">({count})</span>
          </SheetTitle>
          <button
            data-testid="cart-close-btn"
            onClick={() => setOpen(false)}
            className="text-aak-mutedfg hover:text-aak-fg transition-colors"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <p className="font-serif text-3xl text-aak-fg mb-3">Your bag is empty</p>
            <p className="text-sm text-aak-mutedfg mb-8">Add a piece from the collection to begin.</p>
            <button
              onClick={() => setOpen(false)}
              className="text-xs uppercase tracking-[0.25em] text-aak-primary link-underline"
            >
              Continue browsing
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 divide-y divide-aak-border">
              {items.map((item) => (
                <div key={item.key} data-testid={`cart-item-${item.id}`} className="py-6 flex gap-4">
                  <div className="w-20 h-28 bg-aak-muted flex-shrink-0 overflow-hidden">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between">
                      <h4 className="font-serif text-xl text-aak-fg leading-none">{item.name}</h4>
                      <button
                        data-testid={`remove-${item.id}`}
                        onClick={() => removeItem(item.key)}
                        className="text-aak-mutedfg hover:text-aak-fg"
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-aak-mutedfg mt-2">
                      Size {item.size || "One"}
                    </p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-aak-border">
                        <button
                          data-testid={`qty-minus-${item.id}`}
                          onClick={() => updateQty(item.key, -1)}
                          className="w-8 h-8 flex items-center justify-center text-aak-fg hover:bg-aak-muted"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <button
                          data-testid={`qty-plus-${item.id}`}
                          onClick={() => updateQty(item.key, 1)}
                          className="w-8 h-8 flex items-center justify-center text-aak-fg hover:bg-aak-muted"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-sm text-aak-fg">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-aak-border">
              <div className="flex justify-between items-baseline mb-1">
                <span className="text-xs uppercase tracking-[0.2em] text-aak-mutedfg">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-serif text-2xl text-aak-fg">
                  {formatPrice(subtotal)}
                </span>
              </div>
              <p className="text-[11px] text-aak-mutedfg mb-5">Taxes & shipping calculated at checkout.</p>
              <button
                data-testid="checkout-btn"
                onClick={() => setShowDetails(true)}
                className="w-full bg-aak-fg text-aak-bg py-4 text-xs uppercase tracking-[0.25em] hover:bg-aak-primary transition-colors duration-500 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent
          className="max-w-md p-0 gap-0 bg-aak-bg border border-aak-border rounded-none [&>button]:hidden"
          data-testid="checkout-details-dialog"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-aak-border">
            <DialogTitle className="font-serif text-2xl text-aak-fg font-normal">Shipping Details</DialogTitle>
            <button
              data-testid="details-close-btn"
              onClick={() => setShowDetails(false)}
              className="text-aak-mutedfg hover:text-aak-fg"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-aak-mutedfg block mb-1.5">Full Name</label>
              <input data-testid="cust-name" value={cust.name} onChange={(e) => setField("name", e.target.value)} className="form-in" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-aak-mutedfg block mb-1.5">Email</label>
                <input data-testid="cust-email" type="email" value={cust.email} onChange={(e) => setField("email", e.target.value)} className="form-in" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-aak-mutedfg block mb-1.5">Phone</label>
                <input data-testid="cust-phone" value={cust.phone} onChange={(e) => setField("phone", e.target.value)} className="form-in" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-aak-mutedfg block mb-1.5">Shipping Address</label>
              <textarea data-testid="cust-address" rows={3} value={cust.address} onChange={(e) => setField("address", e.target.value)} className="form-in resize-none" />
            </div>
            <div className="flex justify-between items-baseline pt-2">
              <span className="text-xs uppercase tracking-[0.2em] text-aak-mutedfg">Total</span>
              <span className="font-serif text-2xl text-aak-fg">{formatPrice(subtotal)}</span>
            </div>
            <button
              data-testid="pay-now-btn"
              onClick={checkout}
              disabled={loading}
              className="w-full bg-aak-fg text-aak-bg py-4 text-xs uppercase tracking-[0.25em] hover:bg-aak-primary transition-colors duration-500 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pay Securely"}
            </button>
            <p className="text-[11px] text-aak-mutedfg text-center">Secure payment via Razorpay · UPI, Cards & Netbanking</p>
          </div>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
};
